import { buildEvolutionFilters, buildManualPreview, canPreviewOperation, completePreview, contextBelongsTo, contextIsValid, createIdempotencyKey, normalizeFiscalStatus, normalizeOperation } from './nfseWorkflowContract'

const initialState = () => ({ items: [], selectedIds: [], total: 0, totalValue: null, filters: null, operation: null, preview: null, documents: [], documentTotal: 0, message: '', busy: false, documentsBusy: false, sent: false, cancelPending: {}, previewKey: null, manualContextKey: null })
const hasAuthorizedFiscalIdentity = data => {
  const groups = Array.isArray(data?.groups) ? data.groups : []
  const externalIdentity = data?.external_id || data?.provider_reference || data?.numero || data?.numero_nfs
    || data?.nfse_number || data?.rps_numero || data?.codigo_verificacao
  return groups.length > 0 && groups.every(group => normalizeFiscalStatus(group.status) === 'authorized') && Boolean(externalIdentity)
}
const asUnknownOperation = operation => ({
  ...normalizeOperation(operation),
  status: 'unknown',
  groups: (operation?.groups || []).map(group => ({ ...group, status: 'unknown' })),
})
const isExpiredContext = data => {
  const expiresAt = Date.parse(data?.expires_at || '')
  return Number.isFinite(expiresAt) && expiresAt <= Date.now()
}
export default class NfseWorkflow {
  constructor(controller, scope, keyFactory = createIdempotencyKey, attempts = { read: () => null, write: () => {}, remove: () => {} }) {
    this.controller = controller
    this.scope = scope
    this.keyFactory = keyFactory
    this.attempts = attempts
    this.state = initialState()
    this.generation = 0
    this.queryRevision = 0
    this.recoveryRevision = 0
    this.previewRevision = 0
    this.documentRevision = 0
    this.manualPayloadSignature = null
  }
  changeScope(scope) {
    this.generation++
    this.scope = scope
    this.manualPayloadSignature = null
    Object.assign(this.state, initialState())
  }
  dispose() { this.generation++ }
  invalidatePreview(resetContextKey = true) {
    this.previewRevision++
    this.state.preview = null
    this.state.previewKey = null
    if (resetContextKey) {
      this.state.manualContextKey = null
      this.manualPayloadSignature = null
    }
  }
  async search(input) {
    let filters
    try { filters = buildEvolutionFilters(input) } catch (error) { this.state.message = error.message; return }
    const signature = value => JSON.stringify({ ...value, page: undefined, per_page: undefined })
    if (signature(filters) !== signature(this.state.filters)) this.state.selectedIds = []
    this.state.filters = filters
    this.invalidatePreview()
    this.state.operation = null
    const generation = this.generation
    const revision = ++this.queryRevision
    this.state.busy = true
    this.state.message = ''
    const result = await this.controller.listEvolutions(filters)
    if (generation !== this.generation || revision !== this.queryRevision) return
    this.state.busy = false
    if (!result.success || !Array.isArray(result.data?.items)) {
      this.state.items = []
      this.state.total = 0
      this.state.totalValue = null
      this.state.selectedIds = []
      this.state.message = result.message || 'Contrato de consulta não confirmado: lista paginada ausente.'
      return
    }
    if (result.data.items.some(item => item.empresa_id != null && String(item.empresa_id) !== String(this.scope.empresaId))) {
      this.state.items = []
      this.state.selectedIds = []
      this.state.message = 'A consulta retornou contexto empresarial divergente.'
      return
    }
    this.state.items = result.data.items
    this.state.total = Number(result.data.total ?? result.data.pagination?.total ?? 0)
    this.state.totalValue = result.data.total_value ?? null
    // Se um item reaparece bloqueado, não conservar sua seleção anterior.
    const blocked = result.data.items.filter(item => !this.selectable(item)).map(item => Number(item.id))
    this.state.selectedIds = this.state.selectedIds.filter(id => !blocked.includes(id))
  }
  selectable(item) {
    return item?.eligible === true && Number.isSafeInteger(Number(item.id)) && Number(item.id) > 0 && (item.empresa_id == null || String(item.empresa_id) === String(this.scope.empresaId))
  }
  select(item, checked) {
    if (this.state.busy) return
    const id = Number(item.id)
    if (checked && !this.selectable(item)) return
    this.state.selectedIds = this.state.selectedIds.filter(value => value !== id)
    if (checked) this.state.selectedIds.push(id)
    this.invalidatePreview()
    this.state.operation = null
  }
  async prepareContext({ forceFresh = false } = {}) {
    if (this.state.busy || !this.state.selectedIds.length || !this.state.filters) return
    const generation = this.generation
    const selection = [...this.state.selectedIds].sort((a, b) => a - b)
    const identity = `${this.state.filters.agreement_id}:${this.state.filters.date_ini}:${this.state.filters.date_end}:${selection.join(',')}`
    let idempotencyKey
    try {
      const staleContext = this.state.operation && !contextIsValid(this.state.operation, this.scope)
      const persistedKey = this.attempts.read('context', identity)
      const rotateContext = staleContext || (forceFresh && !persistedKey)
      if (rotateContext && typeof this.attempts.remove === 'function') this.attempts.remove('context', identity)
      idempotencyKey = rotateContext ? this.keyFactory() : (persistedKey || this.keyFactory())
      this.attempts.write('context', identity, idempotencyKey)
    } catch (error) {
      this.state.message = 'Não foi possível preservar a identificação do contexto. Operação não enviada.'
      return
    }
    this.state.busy = true
    this.state.message = ''
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const result = await this.controller.createContext({ recurrence_ids: selection, agreement_id: this.state.filters.agreement_id, date_ini: this.state.filters.date_ini, date_end: this.state.filters.date_end, idempotency_key: idempotencyKey })
      if (generation !== this.generation) return
      if (!result.success) {
        this.state.busy = false
        if (!result.uncertain && typeof this.attempts.remove === 'function') this.attempts.remove('context', identity)
        this.state.message = result.message
        return
      }
      if (!contextIsValid(result.data, this.scope)) {
        if (attempt === 0 && isExpiredContext(result.data)) {
          if (typeof this.attempts.remove === 'function') this.attempts.remove('context', identity)
          try {
            idempotencyKey = this.keyFactory()
            this.attempts.write('context', identity, idempotencyKey)
            continue
          } catch (error) {
            this.state.busy = false
            this.state.message = 'Não foi possível preservar a identificação do contexto. Operação não enviada.'
            return
          }
        }
        this.state.busy = false
        if (typeof this.attempts.remove === 'function') this.attempts.remove('context', identity)
        this.state.message = 'Contexto inválido, expirado ou de outra empresa/usuário. Consulte novamente.'
        return
      }
      this.state.busy = false
      this.state.operation = normalizeOperation(result.data)
      return this.state.operation
    }
  }
  async previewManual(draft) {
    if (this.state.busy || this.state.sent) return
    this.invalidatePreview(false)
    let payload
    try { payload = buildManualPreview(draft) } catch (error) { this.state.message = error.message; return }
    const generation = this.generation
    const revision = this.previewRevision
    // Somente em memória: a chave nunca pode ser reutilizada com outro payload.
    const signature = JSON.stringify(payload)
    if (!this.state.manualContextKey || signature !== this.manualPayloadSignature) {
      try { this.state.manualContextKey = this.keyFactory() } catch (error) { this.state.message = error.message; return }
      this.manualPayloadSignature = signature
    }
    this.state.operation = null
    this.state.busy = true
    this.state.message = ''
    const result = await this.controller.createContext({ ...payload, idempotency_key: this.state.manualContextKey })
    if (generation !== this.generation) return
    this.state.busy = false
    if (revision !== this.previewRevision) return
    if (!result.success) { this.state.message = result.message; return }
    if (result.status === 202 || !contextIsValid(result.data, this.scope) || normalizeFiscalStatus(result.data.status) !== 'draft'
      || result.data.mode !== 'manual' || !canPreviewOperation(result.data)) {
      this.state.message = 'Contexto manual não confirmado, expirado ou de outra empresa/usuário.'
      return
    }
    this.state.operation = normalizeOperation(result.data)
    await this.requestPreview({ operation_id: result.data.operation_id, version: result.data.version })
  }
  async previewContext() {
    const operation = this.state.operation
    if (this.state.busy || this.state.sent || !contextIsValid(operation, this.scope) || !canPreviewOperation(operation)) {
      this.state.message = 'Contexto indisponível para preview. Consulte a operação ou prepare uma nova seleção.'
      return
    }
    await this.requestPreview({ operation_id: operation.operation_id, version: operation.version })
  }
  async requestPreview(payload) {
    const context = this.state.operation
    this.invalidatePreview(false)
    const generation = this.generation
    const revision = this.previewRevision
    this.state.busy = true
    this.state.message = ''
    const result = await this.controller.preview(payload)
    if (generation !== this.generation) return
    this.state.busy = false
    if (revision !== this.previewRevision) return
    if (!result.success) { this.state.message = result.message; return }
    // Somente metadados de escopo/validade podem vir do contexto já validado.
    // Grupos, versão, estado e valores devem estar na nova resposta de preview.
    const metadata = context?.mode === 'manual'
      ? { empresa_id: context.empresa_id, expires_at: context.expires_at, mode: context.mode,
        ...(Object.prototype.hasOwnProperty.call(context, 'user_id') ? { user_id: context.user_id } : {}) }
      : {}
    const operation = { ...metadata, ...result.data }
    if (result.status === 202 || !contextIsValid(context, this.scope) || operation.operation_id !== payload.operation_id
      || (context.mode != null && operation.mode !== context.mode) || Number(operation.version) < Number(payload.version)
      || !contextIsValid(operation, this.scope) || !completePreview(operation)) {
      this.state.message = 'Preview fiscal não confirmado, expirado ou de outra empresa/usuário.'
      return
    }
    try { this.state.previewKey = this.keyFactory() } catch (error) { this.state.message = error.message; return }
    this.state.operation = normalizeOperation(operation)
    // Snapshot independente do formulário. Não persiste dados fiscais no navegador.
    this.state.preview = JSON.parse(JSON.stringify(this.state.operation))
  }
  canEmit() {
    return !this.state.busy && !this.state.sent && Boolean(this.state.previewKey) && contextIsValid(this.state.preview, this.scope) && completePreview(this.state.preview)
  }
  async emit(confirmed) {
    if (confirmed !== true || !this.canEmit()) { this.state.message = 'Gere um preview válido e confirme sua conferência antes de emitir.'; return }
    const generation = this.generation
    const recoveryRevision = this.recoveryRevision
    const attempts = this.attempts
    const preview = this.state.preview
    const confirmation = preview.mode === 'manual' ? { confirmation: true } : { confirmed: true }
    const payload = { operation_id: preview.operation_id, version: preview.version, ...confirmation, idempotency_key: this.state.previewKey }
    try {
      if (attempts.read('emit', preview.operation_id)) {
        this.state.sent = true
        this.state.message = 'Esta operação já possui tentativa de emissão. Consulte para reconciliação.'
        return
      }
      attempts.write('emit', preview.operation_id, this.state.previewKey)
    } catch (error) {
      this.state.message = 'Não foi possível preservar a identificação da tentativa neste navegador. Emissão não enviada.'
      return
    }
    this.state.busy = true
    this.state.sent = true
    const result = await this.controller.emit(payload)
    const responseObsolete = generation !== this.generation || recoveryRevision !== this.recoveryRevision
    if (!result.success && !result.uncertain && typeof attempts.remove === 'function') attempts.remove('emit', preview.operation_id)
    if (responseObsolete) return
    this.state.busy = false
    this.invalidatePreview()
    if (!result.success) {
      if (!result.uncertain) {
        this.state.sent = false
        this.state.operation = null
        if (typeof attempts.remove === 'function') attempts.remove('emit', preview.operation_id)
      }
      this.state.message = result.message
      // Mesmo falha local não libera a seleção nem permite retransmissão.
      if (result.uncertain) this.markUnknown()
      return
    }
    if (result.data?.operation_id !== preview.operation_id || !contextBelongsTo({ ...this.state.operation, ...result.data }, this.scope)
      || (result.data.mode != null && result.data.mode !== preview.mode)) {
      this.markUnknown()
      this.state.message = 'Resposta fiscal inconsistente. Consulte a operação para reconciliação.'
      return
    }
    const operation = normalizeOperation({ ...this.state.operation, ...result.data })
    if (operation.status === 'authorized' && !hasAuthorizedFiscalIdentity(operation)) {
      this.markUnknown()
      this.state.message = 'Autorização sem identidade fiscal e estados finais confirmados. Consulte a operação para reconciliação.'
      return
    }
    this.state.operation = operation
    this.state.message = 'Solicitação recebida. Confira o estado de cada grupo na operação.'
  }
  async recover(id, options = {}) {
    if (!id) return
    const generation = this.generation
    const revision = ++this.recoveryRevision
    const previousOperationId = this.state.operation?.operation_id
    const previousSent = this.state.sent
    const sameOperation = String(previousOperationId || '') === String(id)
    this.invalidatePreview()
    this.state.operation = null
    this.state.sent = false
    this.state.busy = true
    this.state.message = ''
    const result = options.preview ? await this.controller.operation(id, { preview: true }) : await this.controller.operation(id)
    if (generation !== this.generation || revision !== this.recoveryRevision) return
    this.state.busy = false
    if (!result.success) { this.state.message = result.message; return }
    if (!contextBelongsTo(result.data, this.scope) || String(result.data.operation_id) !== String(id)) {
      this.state.operation = null
      this.state.message = 'Contexto de operação não confirmado para esta empresa/usuário.'
      return
    }
    const recovered = normalizeOperation(result.data)
    this.state.operation = recovered.status === 'authorized' && !hasAuthorizedFiscalIdentity(recovered)
      ? asUnknownOperation(recovered) : recovered
    try {
      this.state.sent = (sameOperation && previousSent) || Boolean(this.attempts.read('emit', id)) || !canPreviewOperation(this.state.operation)
    } catch (error) {
      this.state.sent = true
      this.state.message = 'Identificação local da tentativa indisponível. Consulte a operação para reconciliação.'
    }
    if (!this.state.sent) this.state.message = contextIsValid(result.data, this.scope)
      ? 'Contexto recuperado. Gere um novo preview e confirme antes de emitir.'
      : 'Contexto expirado ou sem versão válida. Prepare uma nova seleção.'
  }
  markUnknown() {
    this.state.operation = {
      ...this.state.operation, status: 'unknown',
      groups: (this.state.operation?.groups || []).map(group => ['draft', 'preview', 'reserved', 'processing'].includes(group.status) ? { ...group, status: 'unknown' } : group),
    }
  }
  async loadDocuments(filters = {}) {
    const generation = this.generation
    const revision = ++this.documentRevision
    this.state.documentsBusy = true
    const result = await this.controller.documents(filters)
    if (generation !== this.generation || revision !== this.documentRevision) return
    this.state.documentsBusy = false
    if (!result.success || !Array.isArray(result.data?.items)) { this.state.documents = []; this.state.documentTotal = 0; this.state.message = result.message || 'Contrato de documentos não confirmado: paginação ausente.'; return }
    if (result.data.items.some(item => item.empresa_id != null && String(item.empresa_id) !== String(this.scope.empresaId))) { this.state.documents = []; this.state.documentTotal = 0; this.state.message = 'Contexto empresarial divergente nos documentos.'; return }
    this.state.documents = result.data.items.map(item => ({ ...item, status: normalizeFiscalStatus(item.status) }))
    this.state.documentTotal = Number(result.data.total ?? result.data.pagination?.total ?? 0)
  }
  async cancelDocument(document, reason, confirmed) {
    if (this.state.busy || !confirmed || !reason?.trim() || document.status !== 'authorized' || this.state.cancelPending[document.id]) return
    if (document.empresa_id != null && String(document.empresa_id) !== String(this.scope.empresaId)) return
    const generation = this.generation
    const attempts = this.attempts
    let key
    try {
      if (attempts.read('cancel', document.id)) {
        this.state.message = 'Este documento já possui tentativa de cancelamento. Consulte para reconciliação.'
        return
      }
      key = this.keyFactory()
      attempts.write('cancel', document.id, key)
    } catch (error) { this.state.message = 'Não foi possível preservar a identificação do cancelamento. Pedido não enviado.'; return }
    this.state.busy = true
    this.state.cancelPending = { ...this.state.cancelPending, [document.id]: true }
    const result = await this.controller.cancel(document.id, { reason: reason.trim(), confirmed: true, idempotency_key: key })
    if (!result.success && !result.uncertain && typeof attempts.remove === 'function') attempts.remove('cancel', document.id)
    if (generation !== this.generation) return
    this.state.busy = false
    if (!result.success) {
      if (!result.uncertain) {
        const remaining = { ...this.state.cancelPending }
        delete remaining[document.id]
        this.state.cancelPending = remaining
        if (typeof attempts.remove === 'function') attempts.remove('cancel', document.id)
      }
      this.state.message = result.message
      return
    }
    const cancellation = { ...result.data, id: result.data?.id ?? document.id }
    if (String(cancellation.id) !== String(document.id) || (cancellation.empresa_id != null && String(cancellation.empresa_id) !== String(this.scope.empresaId))) {
      this.state.message = 'Resposta de cancelamento inconsistente. Consulte o documento para reconciliação.'
      return
    }
    const status = normalizeFiscalStatus(cancellation.status)
    // Um pedido aceito não permite limpar flags de evoluções nem dados financeiros.
    this.state.documents = this.state.documents.map(item => item.id === document.id
      ? { ...item, ...cancellation, status: ['cancel_requested', 'cancelled'].includes(status) ? status : item.status }
      : item)
    this.state.message = status === 'cancelled' ? 'Cancelamento confirmado pela API.'
      : status === 'cancel_requested' ? 'Cancelamento solicitado. Aguarde a confirmação fiscal e consulte os documentos.'
        : 'Cancelamento não confirmado. A autorização e os vínculos foram preservados; consulte o documento para reconciliação.'
  }
}
